import { supabase } from './supabaseClient';
import { forceCreateUserAccount, simplifiedAccountCreation, runAccountDiagnostics } from './accountDiagnostics';

/**
 * Creates a new account for a user and assigns the Super-Admin role
 * - Works directly with Supabase database for reliable operation
 * - Avoids CORS issues with Edge Functions
 */
export const createUserAccount = async (userId: string, email: string, name: string, accountName: string, accessCode?: string, selectedPlan?: string): Promise<boolean> => {
  try {
    console.log('Creating account for user:', userId, 'with name:', accountName, 'with access code:', accessCode, 'with selected plan:', selectedPlan);

    // First check if user already has an account
    const { data: existingAccounts, error: accountsError } = await supabase
      .from('accounts')
      .select('id')
      .eq('owner_id', userId);

    if (accountsError) {
      console.error('Error checking for existing accounts:', accountsError);
    } else if (existingAccounts && existingAccounts.length > 0) {
      console.log('User already has account(s):', existingAccounts);

      // Check if the user has a role assigned to at least one account
      for (const account of existingAccounts) {
        const { data: existingRole, error: roleError } = await supabase
          .from('user_account_roles')
          .select('id')
          .eq('user_id', userId)
          .eq('account_id', account.id)
          .maybeSingle();

        if (roleError) {
          console.error('Error checking for existing role:', roleError);
        } else if (existingRole) {
          console.log('User already has role for account:', account.id);
          return true; // User is already set up
        } else {
          // User has account but no role, assign one
          console.log('User has account but no role, assigning Super-Admin for account:', account.id);

          // Get Super-Admin role ID
          const { data: roleData, error: roleError } = await supabase
            .from('roles')
            .select('id')
            .eq('name', 'Super-Admin')
            .single();

          if (roleError || !roleData) {
            console.error('Error getting Super-Admin role:', roleError);
          } else {
            // Assign role
            const { error: assignError } = await supabase
              .from('user_account_roles')
              .insert({
                user_id: userId,
                account_id: account.id,
                role_id: roleData.id
              });

            if (assignError) {
              console.error('Error assigning role:', assignError);
            } else {
              console.log('Successfully assigned Super-Admin role for existing account');
              return true;
            }
          }
        }
      }
    }

    // If we get here, user either has no accounts or no roles - create a new account
    console.log('Creating new account for user:', userId);

    // Determine subscription tier based on access code or selected plan
    const isUsingAccessCode = !!accessCode;
    const subscriptionTier = isUsingAccessCode ? 'professional' : (selectedPlan || 'standard');
    const subscriptionStatus = isUsingAccessCode ? 'active' : 'trialing'; // Access code gets active, others get 7-day trial

    // Calculate trial end date (7 days from now) for non-access-code users
    const trialEndDate = isUsingAccessCode
      ? null
      : new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString();

    // Create account with appropriate flags
    const { data: accountData, error: createError } = await supabase
      .from('accounts')
      .insert({
        name: accountName,
        owner_id: userId,
        subscription_tier: subscriptionTier,
        subscription_plan: subscriptionTier,  // Set plan to match tier for new accounts
        subscription_status: subscriptionStatus,
        trial_end_date: trialEndDate,
        is_beta_tester: isUsingAccessCode  // Set beta flag if using access code
      })
      .select()
      .single();
      
    if (createError) {
      console.error('Error creating account:', createError);
      
      // If account creation fails, try the simplified approach
      console.log('Trying simplified account creation as fallback...');
      const result = await simplifiedAccountCreation(userId, email, name);
      
      if (!result.success) {
        console.error('Simplified account creation also failed:', result.error);
        
        // Last resort: try the Edge Function approach
        console.log('Trying Edge Function as last resort...');
        const { success: edgeSuccess } = await forceCreateUserAccount({ 
          user: { id: userId, email }, 
          access_token: null // Will use POST body instead
        });
        
        return edgeSuccess;
      }
      
      return result.success;
    }
    
    console.log('Successfully created account:', accountData.id);
    
    // Get Super-Admin role ID
    const { data: roleData, error: roleError } = await supabase
      .from('roles')
      .select('id')
      .eq('name', 'Super-Admin')
      .single();
      
    if (roleError || !roleData) {
      console.error('Error getting Super-Admin role:', roleError);
      // Continue without role assignment - the trigger should handle it
      console.log('Role assignment will be handled by database trigger');
      return true;
    }
    
    // Assign role
    const { error: assignError } = await supabase
      .from('user_account_roles')
      .insert({
        user_id: userId,
        account_id: accountData.id,
        role_id: roleData.id
      });
      
    if (assignError) {
      console.error('Error assigning role:', assignError);
      return false;
    }
    
    console.log('Successfully assigned Super-Admin role for new account');
    
    // If an access code was provided, redeem it
    if (accessCode) {
      console.log('Redeeming access code:', accessCode);
      
      try {
        const { data: redeemResult, error: redeemError } = await supabase.rpc('redeem_access_code', {
          p_code: accessCode.trim().toUpperCase(),
          p_account_id: accountData.id,
          p_user_id: userId
        });
        
        if (redeemError) {
          console.error('Error redeeming access code:', redeemError);
        } else if (redeemResult && redeemResult.success) {
          console.log('Successfully redeemed access code:', redeemResult);
          
          // Update account with beta access end date if applicable
          if (redeemResult.expires_at) {
            const { error: updateError } = await supabase
              .from('accounts')
              .update({ 
                beta_access_end_date: redeemResult.expires_at
              })
              .eq('id', accountData.id);
              
            if (updateError) {
              console.error('Error updating beta access end date:', updateError);
            }
          }
        } else {
          console.warn('Access code redemption failed:', redeemResult?.error || 'Unknown error');
        }
      } catch (err) {
        console.error('Exception redeeming access code:', err);
      }
    }
    
    // Log the activity
    await supabase
      .from('app_usage_logs')
      .insert({
        user_id: userId,
        account_id: accountData.id,
        activity_type: 'signup',
        metadata: { accountCreated: true, accessCode: accessCode || null }
      });
    
    return true;
  } catch (error) {
    console.error('Exception in account creation:', error);
    return false;
  }
};

/**
 * Checks if a user has an account and role, and creates them if needed
 * - Implements multi-tiered fallback strategies:
 *   1. First tries direct Supabase client operations
 *   2. Falls back to simplified approach with less restrictive policies
 *   3. As last resort, uses Edge Function with admin privileges
 */
export const ensureUserAccount = async (session: any, selectedPlan?: string): Promise<boolean> => {
  if (!session || !session.user) {
    console.error('Invalid session provided to ensureUserAccount');
    return false;
  }

  try {
    const userId = session.user.id;
    const email = session.user.email;

    // Check if user has pending invitations or is in invitation flow
    const userMetadata = session.user.user_metadata || {};
    if (userMetadata.skip_account_creation || userMetadata.type === 'team_invitation') {
      console.log('User is from invitation flow, skipping account creation');
      return true; // Return true to prevent error messages
    }

    // Also check for pending invitations in the database
    const { data: pendingInvites } = await supabase
      .from('invitations')
      .select('id')
      .eq('email', email)
      .eq('status', 'pending')
      .limit(1);

    if (pendingInvites && pendingInvites.length > 0) {
      console.log('User has pending invitations, skipping account creation');
      return true;
    }

    // Get name from user metadata
    const fullName = session.user.user_metadata?.full_name ||
                    session.user.user_metadata?.name ||
                    session.user.email?.split('@')[0] ||
                    'New User';

    // Get access code from metadata if provided during signup
    const accessCode = session.user.user_metadata?.coupon_used ||
                      session.user.user_metadata?.access_code ||
                      null;

    // Create account name without date to ensure consistency
    const accountName = `${fullName}'s Account`;

    // First approach: direct client operation
    const success = await createUserAccount(userId, email, fullName, accountName, accessCode, selectedPlan);
    
    if (success) {
      return true;
    }
    
    // If that fails, use the SQL function through RPC
    console.log('Trying SQL function approach...');
    const { data: sqlResult, error: sqlError } = await supabase.rpc(
      'ensure_user_account',
      { 
        p_user_id: userId,
        p_email: email,
        p_full_name: fullName
      }
    );
    
    if (sqlError) {
      console.error('SQL function approach failed:', sqlError);
      
      // Final fallback: use Edge Function with admin privileges
      console.log('Trying Edge Function as final fallback...');
      const { success: edgeSuccess } = await forceCreateUserAccount(session);
      
      if (!edgeSuccess) {
        // If all efforts fail, run diagnostics to help troubleshoot
        console.log('All approaches failed, running diagnostics...');
        await runAccountDiagnostics(userId);
      }
      
      return edgeSuccess;
    }
    
    return sqlResult?.success || false;
  } catch (error) {
    console.error('Error in ensureUserAccount:', error);
    return false;
  }
};